import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as solidIcons from '@fortawesome/free-solid-svg-icons';
import * as brandIcons from '@fortawesome/free-brands-svg-icons';
import schemaData from '../../data/businessInfo.json';
import '../../css/AdminEditor.css';

// Merge solid and brand icons for lookup
const allIcons = { ...solidIcons, ...brandIcons };

// Environment variables for Gist API
const CONFIG_URL = process.env.REACT_APP_CONFIG_URL;
const GIST_TOKEN = process.env.REACT_APP_GIST_TOKEN;
const GIST_ID = process.env.REACT_APP_GIST_ID;
const GIST_FILENAME = process.env.REACT_APP_GIST_FILENAME;

// Field descriptions for help text - explains what each field/section does
const FIELD_HELP = {
  // Main sections
  'business': 'Root container for all business information',
  'business.name': 'Your business name displayed in header, footer, and browser title',
  'business.tagline': 'Short catchy phrase shown below your logo',
  'business.description': 'Full description used in SEO meta tags and about sections',
  'business.foundedYear': 'Year your business was established',
  'business.teamSize': 'Number of team members (e.g., "50+" or "10-20")',
  
  // Contact Info
  'business.contactInfo': 'All contact details shown on Contact page and footer',
  'business.contactInfo.phone': 'Primary phone number for customers',
  'business.contactInfo.alternatePhone': 'Secondary/WhatsApp number',
  'business.contactInfo.email': 'Primary email address',
  'business.contactInfo.socialMedia': 'Links to your social media profiles',
  'business.contactInfo.socialMedia.whatsappCommunity': 'WhatsApp group/community invite link',
  
  // Statistics
  'business.statistics': 'Key metrics shown on homepage (e.g., Happy Clients, Years Experience)',
  
  // About Section
  'business.about': 'Content for About Us page',
  'business.about.summary': 'Main paragraph about your business on About page',
  'business.about.journey': 'Your business story/history',
  'business.about.coreValues': 'Values displayed with icons on About page',
  'business.about.leadershipTeam': 'Team members shown on About page',
  'business.about.certifications': 'Business certifications and licenses',
  
  // Vision
  'business.vision': 'Content for Vision & Mission page',
  'business.vision.statement': 'Your vision statement',
  'business.vision.mission': 'Your mission statement',
  'business.vision.strategicGoals': 'Long-term goals shown on Vision page',
  
  // Services
  'business.services': 'Services listed on Services page',
  'business.features': 'Key features shown on homepage "Why Choose Us" section',
  'business.process': 'Your work process steps shown on Services page',
  
  // Products
  'business.products': 'Product catalog shown on Products page',
  'business.products.enabled': 'Show/hide the Products page and menu item',
  'business.products.pageTitle': 'Title shown at top of Products page',
  'business.products.categories': 'Product categories for filtering',
  'business.products.items': 'Individual products in your catalog',
  
  // Donation
  'business.donation': 'Donation functionality configuration',
  'business.donation.enabled': 'Show/hide the Donation page and menu item',
  'business.donation.amounts': 'Preset donation amounts users can select',
  'business.donation.currency': 'Currency for donations (e.g., INR, USD)',
  
  // UI Theme
  'business.ui': 'User interface customization options',
  'business.ui.theme': 'Color scheme for your website',
  'business.ui.theme.primaryColor': 'Main brand color used for buttons, links, headers',
  'business.ui.theme.secondaryColor': 'Secondary color for text and accents',
  'business.ui.theme.primaryColorDark': 'Darker shade of primary color for hover states',
  'business.ui.theme.logoColor': 'Color applied to SVG logo (if used)',
  
  // Images
  'business.images': 'Image URLs for logo, backgrounds, and content',
  'business.images.logo': 'Your business logo URL (rectangular recommended)',
  'business.images.favicon': 'Browser tab icon (ICO or PNG)',
  'business.images.backgrounds': 'Background images for page hero sections',
  
  // Careers
  'business.careers': 'Job listings and career page content',
  'business.careers.enabled': 'Show/hide the Careers section on About page',
  'business.careers.openPositions': 'Current job openings',
  
  // Location
  'business.location': 'Map location for Contact page',
  'business.location.enabled': 'Show/hide the map on Contact page',
  
  // Legal Info
  'business.legalInfo': 'Business registration details shown on footer/legal pages',
  'business.legalInfo.gstin': 'GST Identification Number',
  'business.legalInfo.panNumber': 'PAN Card number',
  'business.legalInfo.customFields': 'Add any other licenses or registrations (e.g., Shop Act, Halal Cert)',
};

// Deep merge: schema provides STRUCTURE, gist provides VALUES
// Fields not in gist will be empty (not filled with schema sample values)
const deepMerge = (schema, data) => {
  // If data is undefined/null, return empty structure based on schema type
  if (data === undefined || data === null) {
    if (Array.isArray(schema)) {
      return []; // Empty array
    }
    if (typeof schema === 'object' && schema !== null) {
      // Create object with same structure but empty values
      const result = {};
      for (const key in schema) {
        result[key] = deepMerge(schema[key], undefined);
      }
      return result;
    }
    // Primitive: return type-appropriate empty value
    if (typeof schema === 'boolean') return false;
    if (typeof schema === 'number') return 0;
    return ''; // Empty string for text fields
  }
  
  if (Array.isArray(schema)) {
    // For arrays, use data array - only include items that exist in gist
    if (Array.isArray(data) && data.length > 0) {
      const schemaItem = schema[0];
      // Keep gist data, but ensure each item has all schema keys
      return data.map(item => {
        if (typeof schemaItem === 'object' && schemaItem !== null) {
          // Merge each item to ensure all schema keys exist
          const mergedItem = {};
          for (const key in schemaItem) {
            mergedItem[key] = item?.[key] !== undefined ? item[key] : getEmptyValue(schemaItem[key]);
          }
          // Also keep any extra keys from data
          for (const key in item) {
            if (!(key in mergedItem)) {
              mergedItem[key] = item[key];
            }
          }
          return mergedItem;
        }
        return item;
      });
    }
    return []; // Empty array if no data
  }
  
  if (typeof schema === 'object' && schema !== null) {
    const result = {};
    // Include all keys from schema
    for (const key in schema) {
      if (data?.[key] !== undefined) {
        // Data exists - recurse to handle nested structures
        result[key] = deepMerge(schema[key], data[key]);
      } else {
        // No data for this key - use empty value
        result[key] = getEmptyValue(schema[key]);
      }
    }
    // Also include any extra keys from data not in schema
    for (const key in data) {
      if (!(key in result)) {
        result[key] = data[key];
      }
    }
    return result;
  }
  
  // Primitive value - use data directly
  return data;
};

// Get empty value based on schema type
const getEmptyValue = (schema) => {
  if (Array.isArray(schema)) return [];
  if (typeof schema === 'object' && schema !== null) {
    const result = {};
    for (const key in schema) {
      result[key] = getEmptyValue(schema[key]);
    }
    return result;
  }
  if (typeof schema === 'boolean') return false;
  if (typeof schema === 'number') return 0;
  return ''; // Empty string for text
};

// Common Font Awesome icons for picker (solid icons)
const FA_ICONS = [
  // Business & Money
  { name: 'faHandshake', label: 'Handshake' },
  { name: 'faBriefcase', label: 'Briefcase' },
  { name: 'faChartLine', label: 'Chart Line' },
  { name: 'faChartPie', label: 'Chart Pie' },
  { name: 'faChartBar', label: 'Chart Bar' },
  { name: 'faMoneyBill', label: 'Money Bill' },
  { name: 'faDollarSign', label: 'Dollar Sign' },
  { name: 'faCreditCard', label: 'Credit Card' },
  { name: 'faWallet', label: 'Wallet' },
  { name: 'faBuilding', label: 'Building' },
  { name: 'faStore', label: 'Store' },
  { name: 'faShop', label: 'Shop' },
  
  // People & Users
  { name: 'faUser', label: 'User' },
  { name: 'faUsers', label: 'Users' },
  { name: 'faUserTie', label: 'User Tie' },
  { name: 'faUserGroup', label: 'User Group' },
  { name: 'faPeopleGroup', label: 'People Group' },
  
  // Technology
  { name: 'faLaptopCode', label: 'Laptop Code' },
  { name: 'faDesktop', label: 'Desktop' },
  { name: 'faMobileAlt', label: 'Mobile' },
  { name: 'faCode', label: 'Code' },
  { name: 'faDatabase', label: 'Database' },
  { name: 'faServer', label: 'Server' },
  { name: 'faCloud', label: 'Cloud' },
  { name: 'faGlobe', label: 'Globe' },
  { name: 'faWifi', label: 'Wifi' },
  
  // Ideas & Innovation
  { name: 'faLightbulb', label: 'Lightbulb' },
  { name: 'faRocket', label: 'Rocket' },
  { name: 'faBolt', label: 'Bolt' },
  { name: 'faMagicWandSparkles', label: 'Magic Wand' },
  { name: 'faPuzzlePiece', label: 'Puzzle Piece' },
  
  // Communication
  { name: 'faComments', label: 'Comments' },
  { name: 'faComment', label: 'Comment' },
  { name: 'faEnvelope', label: 'Envelope' },
  { name: 'faPhone', label: 'Phone' },
  { name: 'faBullhorn', label: 'Bullhorn' },
  { name: 'faMegaphone', label: 'Megaphone' },
  
  // Nature & Agriculture
  { name: 'faLeaf', label: 'Leaf' },
  { name: 'faSeedling', label: 'Seedling' },
  { name: 'faTree', label: 'Tree' },
  { name: 'faCow', label: 'Cow' },
  { name: 'faWheatAwn', label: 'Wheat' },
  { name: 'faTractor', label: 'Tractor' },
  { name: 'faSun', label: 'Sun' },
  { name: 'faWater', label: 'Water' },
  { name: 'faMountain', label: 'Mountain' },
  
  // Food & Drink  
  { name: 'faUtensils', label: 'Utensils' },
  { name: 'faMugHot', label: 'Mug Hot' },
  { name: 'faCoffee', label: 'Coffee' },
  { name: 'faWineGlass', label: 'Wine Glass' },
  { name: 'faBowlFood', label: 'Bowl Food' },
  
  // Actions & Status
  { name: 'faCheck', label: 'Check' },
  { name: 'faCheckCircle', label: 'Check Circle' },
  { name: 'faStar', label: 'Star' },
  { name: 'faHeart', label: 'Heart' },
  { name: 'faThumbsUp', label: 'Thumbs Up' },
  { name: 'faAward', label: 'Award' },
  { name: 'faTrophy', label: 'Trophy' },
  { name: 'faMedal', label: 'Medal' },
  { name: 'faCertificate', label: 'Certificate' },
  
  // Objects
  { name: 'faCog', label: 'Cog/Settings' },
  { name: 'faGear', label: 'Gear' },
  { name: 'faWrench', label: 'Wrench' },
  { name: 'faTools', label: 'Tools' },
  { name: 'faHome', label: 'Home' },
  { name: 'faHouse', label: 'House' },
  { name: 'faKey', label: 'Key' },
  { name: 'faLock', label: 'Lock' },
  { name: 'faShield', label: 'Shield' },
  { name: 'faBook', label: 'Book' },
  { name: 'faGraduationCap', label: 'Graduation Cap' },
  
  // Arrows & Navigation
  { name: 'faArrowRight', label: 'Arrow Right' },
  { name: 'faArrowUp', label: 'Arrow Up' },
  { name: 'faArrowTrendUp', label: 'Arrow Trend Up' },
  { name: 'faCircleArrowRight', label: 'Circle Arrow Right' },
  
  // Time & Calendar
  { name: 'faClock', label: 'Clock' },
  { name: 'faCalendar', label: 'Calendar' },
  { name: 'faCalendarDays', label: 'Calendar Days' },
  { name: 'faHourglassHalf', label: 'Hourglass' },
  
  // Misc
  { name: 'faInfoCircle', label: 'Info Circle' },
  { name: 'faQuestionCircle', label: 'Question Circle' },
  { name: 'faExclamationCircle', label: 'Exclamation Circle' },
  { name: 'faMapMarkerAlt', label: 'Map Marker' },
  { name: 'faLocationDot', label: 'Location Dot' },
  { name: 'faFlag', label: 'Flag' },
  { name: 'faBell', label: 'Bell' },
  { name: 'faGift', label: 'Gift' },
  { name: 'faHandHoldingHeart', label: 'Hand Holding Heart' },
  { name: 'faPrayingHands', label: 'Praying Hands' },
  { name: 'faOm', label: 'Om' },
  
  // Social Media (Brand Icons)
  { name: 'faFacebook', label: 'Facebook', brand: true },
  { name: 'faFacebookF', label: 'Facebook F', brand: true },
  { name: 'faTwitter', label: 'Twitter', brand: true },
  { name: 'faXTwitter', label: 'X (Twitter)', brand: true },
  { name: 'faInstagram', label: 'Instagram', brand: true },
  { name: 'faLinkedin', label: 'LinkedIn', brand: true },
  { name: 'faLinkedinIn', label: 'LinkedIn In', brand: true },
  { name: 'faYoutube', label: 'YouTube', brand: true },
  { name: 'faWhatsapp', label: 'WhatsApp', brand: true },
  { name: 'faTelegram', label: 'Telegram', brand: true },
  { name: 'faPinterest', label: 'Pinterest', brand: true },
  { name: 'faSnapchat', label: 'Snapchat', brand: true },
  { name: 'faTiktok', label: 'TikTok', brand: true },
  { name: 'faThreads', label: 'Threads', brand: true },
  { name: 'faGoogle', label: 'Google', brand: true },
  { name: 'faGithub', label: 'GitHub', brand: true },
];

// Detect field type based on key name and value
const getFieldType = (key, value, parentPath = '') => {
  const fullPath = parentPath ? `${parentPath}.${key}` : key;
  const lowerKey = key.toLowerCase();
  
  // Icon fields
  if (lowerKey === 'icon') return 'icon';
  
  // Boolean fields
  if (typeof value === 'boolean') return 'checkbox';
  if (lowerKey === 'enabled' || lowerKey === 'isfeatured') return 'checkbox';
  
  // Color fields
  if (lowerKey.includes('color')) {
    return 'color';
  }
  
  // Number fields
  if (typeof value === 'number') return 'number';
  if (lowerKey === 'foundedyear' || lowerKey === 'lat' || lowerKey === 'lng' || lowerKey === 'zoom') {
    return 'number';
  }
  
  // URL fields
  if (lowerKey === 'url' || lowerKey.includes('link') || lowerKey === 'image' || lowerKey === 'photo') {
    return 'url';
  }
  if (typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'))) {
    return 'url';
  }
  
  // Email fields
  if (lowerKey === 'email' || lowerKey.includes('email')) return 'email';
  
  // Textarea for long text
  if (typeof value === 'string' && (value.length > 100 || value.includes('\n'))) {
    return 'textarea';
  }
  
  // Default to text
  return 'text';
};

// Format key for display
const formatLabel = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .replace(/_/g, ' ');
};

// Field Editor Component
const FieldEditor = ({ fieldKey, value, onChange, parentPath = '' }) => {
  const fieldType = getFieldType(fieldKey, value, parentPath);
  const label = formatLabel(fieldKey);
  const fullPath = parentPath ? `${parentPath}.${fieldKey}` : fieldKey;
  const helpText = FIELD_HELP[fullPath];
  const lowerKey = fieldKey.toLowerCase();
  const isEnableToggle = lowerKey === 'enabled' || lowerKey === 'isfeatured';
  
  const handleChange = (e) => {
    let newValue = e.target.value;
    if (fieldType === 'checkbox') {
      newValue = e.target.checked;
    } else if (fieldType === 'number') {
      newValue = e.target.value === '' ? '' : Number(e.target.value);
    }
    onChange(newValue);
  };
  
  // Toggle switch for enabled/featured fields
  if (isEnableToggle || fieldType === 'checkbox') {
    return (
      <div className={`field-editor toggle-field ${isEnableToggle ? 'enable-toggle' : ''}`}>
        <div className="toggle-row">
          <label className="toggle-switch">
            <input
              type="checkbox"
              id={fullPath}
              checked={!!value}
              onChange={handleChange}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label">{label}</span>
          {value && <span className="toggle-status enabled">Enabled</span>}
          {!value && <span className="toggle-status disabled">Disabled</span>}
        </div>
        {helpText && <div className="field-help">{helpText}</div>}
      </div>
    );
  }
  
  return (
    <div className="field-editor">
      <label htmlFor={fullPath}>{label}</label>
      {helpText && <div className="field-help">{helpText}</div>}
      {fieldType === 'textarea' ? (
        <textarea
          id={fullPath}
          value={value || ''}
          onChange={handleChange}
          rows={4}
          placeholder={`Enter ${label.toLowerCase()}...`}
        />
      ) : fieldType === 'color' ? (
        <div className="color-input-wrapper">
          <input
            type="color"
            id={fullPath}
            value={value || '#000000'}
            onChange={handleChange}
          />
          <input
            type="text"
            value={value || ''}
            onChange={handleChange}
            placeholder="#000000"
            className="color-text"
          />
        </div>
      ) : fieldType === 'icon' ? (
        <div className="icon-picker">
          <div className="icon-preview">
            {value && allIcons[value] && (
              <FontAwesomeIcon icon={allIcons[value]} size="2x" />
            )}
            {(!value || !allIcons[value]) && (
              <span className="no-icon">No icon</span>
            )}
          </div>
          <select
            id={fullPath}
            value={value || ''}
            onChange={handleChange}
            className="icon-select"
          >
            <option value="">-- Select an icon --</option>
            <optgroup label="Business & Money">
              {FA_ICONS.filter((_, i) => i < 12).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="People & Users">
              {FA_ICONS.filter((_, i) => i >= 12 && i < 17).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Technology">
              {FA_ICONS.filter((_, i) => i >= 17 && i < 26).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Ideas & Innovation">
              {FA_ICONS.filter((_, i) => i >= 26 && i < 31).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Communication">
              {FA_ICONS.filter((_, i) => i >= 31 && i < 37).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Nature & Agriculture">
              {FA_ICONS.filter((_, i) => i >= 37 && i < 46).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Food & Drink">
              {FA_ICONS.filter((_, i) => i >= 46 && i < 51).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Actions & Status">
              {FA_ICONS.filter((_, i) => i >= 51 && i < 60).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Objects">
              {FA_ICONS.filter((_, i) => i >= 60 && i < 71).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Arrows & Navigation">
              {FA_ICONS.filter((_, i) => i >= 71 && i < 75).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Time & Calendar">
              {FA_ICONS.filter((_, i) => i >= 75 && i < 79).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Misc">
              {FA_ICONS.filter((_, i) => i >= 79 && i < 91).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
            <optgroup label="Social Media (Brand)">
              {FA_ICONS.filter(icon => icon.brand).map(icon => (
                <option key={icon.name} value={icon.name}>{icon.label}</option>
              ))}
            </optgroup>
          </select>
          <input
            type="text"
            value={value || ''}
            onChange={handleChange}
            placeholder="Or type icon name (e.g., faHeart)"
            className="icon-text"
          />
        </div>
      ) : (
        <input
          type={fieldType}
          id={fullPath}
          value={value || ''}
          onChange={handleChange}
          placeholder={`Enter ${label.toLowerCase()}...`}
        />
      )}
    </div>
  );
};

// Array Item Editor Component
const ArrayItemEditor = ({ item, index, schema, onChange, onDelete, parentPath }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const itemPath = `${parentPath}[${index}]`;
  
  // Get a display title for the item
  const getItemTitle = () => {
    if (item.title) return item.title;
    if (item.name) return item.name;
    if (item.id) return item.id;
    return `Item ${index + 1}`;
  };
  
  const handleFieldChange = (key, newValue) => {
    const updatedItem = { ...item, [key]: newValue };
    onChange(updatedItem);
  };
  
  if (typeof item !== 'object' || item === null) {
    // Simple value in array
    return (
      <div className="array-item simple">
        <input
          type="text"
          value={item || ''}
          onChange={(e) => onChange(e.target.value)}
        />
        <button type="button" className="btn-delete" onClick={onDelete}>
          <FontAwesomeIcon icon={solidIcons.faTrash} />
        </button>
      </div>
    );
  }
  
  return (
    <div className="array-item">
      <div className="array-item-header" onClick={() => setIsExpanded(!isExpanded)}>
        <span className="expand-icon">
          <FontAwesomeIcon icon={isExpanded ? solidIcons.faChevronDown : solidIcons.faChevronRight} />
        </span>
        <span className="item-title">{getItemTitle()}</span>
        <button 
          type="button" 
          className="btn-delete" 
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
        >
          <FontAwesomeIcon icon={solidIcons.faTrash} />
        </button>
      </div>
      {isExpanded && (
        <div className="array-item-content">
          {Object.keys(schema || item).map(key => {
            const fieldValue = item[key];
            
            // Handle nested arrays
            if (Array.isArray(schema?.[key]) || Array.isArray(fieldValue)) {
              return (
                <ArrayEditor
                  key={key}
                  fieldKey={key}
                  items={fieldValue || []}
                  schema={schema?.[key]?.[0]}
                  onChange={(newItems) => handleFieldChange(key, newItems)}
                  parentPath={`${itemPath}.${key}`}
                />
              );
            }
            
            // Handle nested objects
            if (typeof fieldValue === 'object' && fieldValue !== null && !Array.isArray(fieldValue)) {
              return (
                <ObjectEditor
                  key={key}
                  fieldKey={key}
                  data={fieldValue}
                  schema={schema?.[key]}
                  onChange={(newData) => handleFieldChange(key, newData)}
                  parentPath={`${itemPath}.${key}`}
                />
              );
            }
            
            // Simple field
            return (
              <FieldEditor
                key={key}
                fieldKey={key}
                value={fieldValue}
                onChange={(newValue) => handleFieldChange(key, newValue)}
                parentPath={itemPath}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// Array Editor Component
const ArrayEditor = ({ fieldKey, items, schema, onChange, parentPath = '' }) => {
  const label = formatLabel(fieldKey);
  const fullPath = parentPath ? `${parentPath}.${fieldKey}` : fieldKey;
  
  const handleItemChange = (index, newItem) => {
    const newItems = [...items];
    newItems[index] = newItem;
    onChange(newItems);
  };
  
  const handleItemDelete = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
  };
  
  const handleAddItem = () => {
    let newItem;
    if (schema && typeof schema === 'object') {
      // Create new item based on schema structure
      newItem = deepMerge(schema, {});
    } else if (items.length > 0 && typeof items[0] === 'object') {
      // Clone structure from first item
      newItem = deepMerge(items[0], {});
    } else {
      // Simple string array
      newItem = '';
    }
    onChange([...items, newItem]);
  };
  
  // Check if this is a simple string array
  const isSimpleArray = items.length === 0 || typeof items[0] !== 'object';
  
  if (isSimpleArray && items.every(item => typeof item === 'string')) {
    // Render as comma-separated or tag-style
    return (
      <div className="array-editor simple-array">
        <label>{label}</label>
        <div className="simple-array-items">
          {items.map((item, index) => (
            <div key={index} className={`simple-array-item ${item.length > 50 || fieldKey.toLowerCase().includes('paragraph') || fieldKey.toLowerCase().includes('description') ? 'block-item' : ''}`}>
              {item.length > 50 || fieldKey.toLowerCase().includes('paragraph') || fieldKey.toLowerCase().includes('description') ? (
                <textarea
                  value={item}
                  onChange={(e) => handleItemChange(index, e.target.value)}
                  rows={3}
                  className="array-textarea"
                  placeholder="Enter text..."
                />
              ) : (
                <input
                  type="text"
                  value={item}
                  onChange={(e) => handleItemChange(index, e.target.value)}
                />
              )}
              <button type="button" className="btn-delete-small" onClick={() => handleItemDelete(index)}>
                <FontAwesomeIcon icon={solidIcons.faTimes} />
              </button>
            </div>
          ))}
        </div>
        <button type="button" className="btn-add-small" onClick={handleAddItem} style={{ marginTop: '8px' }}>
          <FontAwesomeIcon icon={solidIcons.faPlus} /> Add
        </button>
      </div>
    );
  }
  
  return (
    <div className="array-editor">
      <div className="array-header">
        <div className="header-content">
          <h4>{label}</h4>
          {FIELD_HELP[fullPath] && <p className="section-help">{FIELD_HELP[fullPath]}</p>}
        </div>
        <span className="array-count">{items.length} items</span>
      </div>
      <div className="array-items">
        {items.map((item, index) => (
          <ArrayItemEditor
            key={index}
            item={item}
            index={index}
            schema={schema}
            onChange={(newItem) => handleItemChange(index, newItem)}
            onDelete={() => handleItemDelete(index)}
            parentPath={fullPath}
          />
        ))}
      </div>
      <button type="button" className="btn-add" onClick={handleAddItem}>
        <FontAwesomeIcon icon={solidIcons.faPlus} /> Add New {formatLabel(fieldKey).replace(/s$/, '')}
      </button>
    </div>
  );
};

// Object Editor Component
const ObjectEditor = ({ fieldKey, data, schema, onChange, parentPath = '', level = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(level < 2);
  const label = formatLabel(fieldKey);
  const fullPath = parentPath ? `${parentPath}.${fieldKey}` : fieldKey;
  const helpText = FIELD_HELP[fullPath];
  
  const handleFieldChange = (key, newValue) => {
    onChange({ ...data, [key]: newValue });
  };
  
  const keys = Object.keys(schema || data || {});
  
  return (
    <div className={`object-editor level-${level}`}>
      <div className="object-header" onClick={() => setIsExpanded(!isExpanded)}>
        <span className="expand-icon">
          <FontAwesomeIcon icon={isExpanded ? solidIcons.faChevronDown : solidIcons.faChevronRight} />
        </span>
        <div className="header-content">
          <h3>{label}</h3>
          {helpText && <p className="section-help">{helpText}</p>}
        </div>
      </div>
      {isExpanded && (
        <div className="object-content">
          {keys.map(key => {
            const fieldValue = data?.[key];
            const schemaValue = schema?.[key];
            
            // Skip certain internal fields
            if (key.startsWith('_')) return null;
            
            // Handle arrays
            if (Array.isArray(schemaValue) || Array.isArray(fieldValue)) {
              return (
                <ArrayEditor
                  key={key}
                  fieldKey={key}
                  items={fieldValue || []}
                  schema={schemaValue?.[0]}
                  onChange={(newItems) => handleFieldChange(key, newItems)}
                  parentPath={fullPath}
                />
              );
            }
            
            // Handle nested objects
            if (typeof schemaValue === 'object' && schemaValue !== null) {
              return (
                <ObjectEditor
                  key={key}
                  fieldKey={key}
                  data={fieldValue || {}}
                  schema={schemaValue}
                  onChange={(newData) => handleFieldChange(key, newData)}
                  parentPath={fullPath}
                  level={level + 1}
                />
              );
            }
            
            // Simple field
            return (
              <FieldEditor
                key={key}
                fieldKey={key}
                value={fieldValue}
                onChange={(newValue) => handleFieldChange(key, newValue)}
                parentPath={fullPath}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// Main Admin Editor Component
const AdminEditor = () => {
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  
  // Load data from Gist
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (!CONFIG_URL) {
        throw new Error('CONFIG_URL is not set');
      }
      
      // Fetch current data from gist
      const cacheBuster = `?t=${Date.now()}`;
      const response = await fetch(CONFIG_URL + cacheBuster);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch config: ${response.status}`);
      }
      
      const gistData = await response.json();
      
      // Merge schema with gist data
      const mergedData = deepMerge(schemaData, gistData);
      setFormData(mergedData);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
      // Fall back to schema as starting point
      setFormData(schemaData);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  // Save data to Gist
  const handleSave = async () => {
    if (!GIST_TOKEN || !GIST_ID || !GIST_FILENAME) {
      toast.error('Gist configuration is incomplete. Check REACT_APP_GIST_TOKEN, REACT_APP_GIST_ID, and REACT_APP_GIST_FILENAME.');
      return;
    }
    
    setSaving(true);
    
    try {
      // Validate JSON
      const jsonString = JSON.stringify(formData, null, 2);
      JSON.parse(jsonString); // Verify it's valid
      
      // Update Gist via GitHub API
      const response = await fetch(`https://api.github.com/gists/${GIST_ID}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${GIST_TOKEN}`,
          'Content-Type': 'application/json',
          'Accept': 'application/vnd.github.v3+json'
        },
        body: JSON.stringify({
          files: {
            [GIST_FILENAME]: {
              content: jsonString
            }
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to save: ${response.status}`);
      }
      
      toast.success('Changes saved successfully! The website will update shortly.');
      
    } catch (err) {
      console.error('Error saving:', err);
      toast.error(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };
  
  // Handle form data change
  const handleDataChange = (newData) => {
    setFormData(newData);
  };
  
  if (loading) {
    return (
      <div className="admin-editor">
        <div className="admin-loading">
          <FontAwesomeIcon icon={solidIcons.faSpinner} spin size="2x" />
          <p>Loading configuration...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-editor">
      <div className="admin-header">
        <h1>
          <FontAwesomeIcon icon={solidIcons.faCog} /> Business Configuration Editor
        </h1>
        <p>Edit your business information below. Changes will be saved to your configuration.</p>
        {error && (
          <div className="admin-warning">
            <FontAwesomeIcon icon={solidIcons.faExclamationTriangle} />
            <span>Could not load remote config: {error}. Showing default schema.</span>
          </div>
        )}
      </div>
      
      <div className="admin-toolbar">
        <button 
          type="button" 
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <FontAwesomeIcon icon={solidIcons.faSpinner} spin /> Saving...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={solidIcons.faSave} /> Save Changes
            </>
          )}
        </button>
        <button 
          type="button" 
          className="btn btn-secondary"
          onClick={() => loadData()}
          disabled={loading}
        >
          <FontAwesomeIcon icon={solidIcons.faSync} /> Reload
        </button>
        <button 
          type="button" 
          className={`btn btn-outline ${showJsonPreview ? 'active' : ''}`}
          onClick={() => setShowJsonPreview(!showJsonPreview)}
        >
          <FontAwesomeIcon icon={solidIcons.faCode} /> {showJsonPreview ? 'Hide' : 'Show'} JSON
        </button>
      </div>
      
      {showJsonPreview && (
        <div className="json-preview">
          <h3>JSON Preview</h3>
          <pre>{JSON.stringify(formData, null, 2)}</pre>
        </div>
      )}
      
      <form className="admin-form" onSubmit={(e) => e.preventDefault()}>
        {formData && formData.business && (
          <ObjectEditor
            fieldKey="business"
            data={formData.business}
            schema={schemaData.business}
            onChange={(newData) => handleDataChange({ ...formData, business: newData })}
            level={0}
          />
        )}
      </form>
      
      <div className="admin-footer">
        <button 
          type="button" 
          className="btn btn-primary btn-lg"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <FontAwesomeIcon icon={solidIcons.faSpinner} spin /> Saving...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={solidIcons.faSave} /> Save All Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AdminEditor;
